import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { OSProvider } from './OSContext';
import BootScreen from './BootScreen';
import LoginScreen from './LoginScreen';
import Desktop from './Desktop';

const BOOT_DURATION_MS = 1500;

/**
 * Self-contained boot -> login -> desktop state machine for OS mode.
 * Rendered directly by App.jsx when theme === 'os', bypassing the
 * legacy/v2 Spline loading gate entirely.
 */
const OSApp = ({ theme, onSetTheme }) => {
	const [stage, setStage] = useState('boot'); // 'boot' | 'login' | 'desktop'
	const [progress, setProgress] = useState(0);

	useEffect(() => {
		if (stage !== 'boot') return undefined;
		const start = Date.now();
		const interval = setInterval(() => {
			const pct = Math.min(100, Math.round(((Date.now() - start) / BOOT_DURATION_MS) * 100));
			setProgress(pct);
			if (pct >= 100) {
				clearInterval(interval);
				setTimeout(() => setStage('login'), 250);
			}
		}, 30);
		return () => clearInterval(interval);
	}, [stage]);

	return (
		<OSProvider theme={theme} onThemeChange={onSetTheme}>
			<div className="fixed inset-0 z-40 bg-black">
				<AnimatePresence mode="wait">
					{stage === 'boot' && (
						<motion.div key="boot" exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="absolute inset-0">
							<BootScreen progress={progress} />
						</motion.div>
					)}
					{stage === 'login' && (
						<motion.div key="login" exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="absolute inset-0">
							<LoginScreen onUnlock={() => setStage('desktop')} />
						</motion.div>
					)}
					{stage === 'desktop' && (
						<motion.div
							key="desktop"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ duration: 0.5 }}
							className="absolute inset-0"
						>
							<Desktop autoOpenAppId="arcade" />
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</OSProvider>
	);
};

export default OSApp;
